// Loads the customer's project from the backend.
//
// The legacy backend (kitchenos-api/modules/customer_portal.js) authenticates
// the customer via an HttpOnly cookie set by `/api/customer/login`. There is
// no token-based endpoint at the time of writing — the chrome layer here
// reads a magic-link token from the URL and forwards it via:
//   1. `?token=<token>` query string (so a future endpoint can pick it up
//      without changing the path), and
//   2. `Authorization: Bearer <token>` header (the conventional shape for
//      a future tokenised endpoint).
//
// If the backend returns 401 / 404 / a non-JSON body, we surface a
// friendly error to the chrome (which renders the "link expired" screen).
// In the absence of a tokenised endpoint, the chrome's mount logic catches
// that and falls back to a demo project so the portal is still
// demo-able locally.

import { api, ApiError } from "@tkos/api-client";
import { formatAddress } from "../types";
import type {
  CustomerProject,
  CustomerQuote,
  CustomerQuoteItem,
  JourneyStage,
  ProjectJourney,
  RetailerBranding,
} from "../types";

/** Server response shape for `GET /api/customer/project`. */
interface ProjectEnvelope {
  ok: boolean;
  customer?: unknown;
  project?: unknown;
  error?: string;
  customerRenderEnabled?: boolean;
}

/**
 * Fetch the customer's project. Throws `ApiError` on non-2xx responses so
 * the caller can distinguish 401/404 (invalid link) from network errors.
 *
 * Auth flow (2026-05-20):
 *   1. POST /api/customer/wizard-login {token}  — exchanges the magic-
 *      link token for an HttpOnly `kos_customer` session cookie. The
 *      cookie travels back via api-client's `credentials: "include"`.
 *      Sets up the session for every subsequent call.
 *   2. GET  /api/customer/project              — reads the session
 *      cookie, returns the full project envelope.
 *
 * On a refresh (cookie already present from a previous tab) the
 * wizard-login call is still cheap and idempotent server-side, so we
 * always run it before the GET. Avoids a stale-cookie edge case where a
 * customer paste-loads a new token in a tab that still has an old
 * session for a different account.
 */
export async function loadCustomerProject(
  token: string,
  signal: AbortSignal,
): Promise<CustomerProject> {
  // Step 1 — exchange the token for a session cookie.
  // wizard-login returns 4xx for an invalid/expired token; let the
  // ApiError bubble so the chrome renders "expired link".
  await api<{ ok: boolean; customer?: unknown; error?: string }>(
    `/customer/wizard-login`,
    {
      method: "POST",
      body: JSON.stringify({ token }),
      signal,
    },
  );

  // Step 2 — fetch the project using the freshly-issued cookie.
  const env = await api<ProjectEnvelope>(`/customer/project`, {
    method: "GET",
    signal,
  });

  if (!env.ok || !env.project || !env.customer) {
    throw new ApiError(404, env.error || "No project for this link");
  }

  return normaliseProject(env);
}

/**
 * Fetch the customer's project using an EXISTING `kos_customer` session
 * cookie (set by POST /api/customer/login or a prior wizard-login). Unlike
 * `loadCustomerProject`, there is no token to exchange — the returning
 * customer is already authenticated. Throws `ApiError` (401/404) if the
 * session is missing or expired so the caller can fall back to the login
 * screen.
 */
export async function loadCustomerProjectFromSession(
  signal?: AbortSignal,
): Promise<CustomerProject> {
  // Build init conditionally — `exactOptionalPropertyTypes` forbids
  // passing `signal: undefined` (RequestInit wants AbortSignal | null).
  const init: RequestInit = { method: "GET" };
  if (signal) init.signal = signal;
  const env = await api<ProjectEnvelope>(`/customer/project`, init);

  if (!env.ok || !env.project || !env.customer) {
    throw new ApiError(404, env.error || "No project for this session");
  }

  return normaliseProject(env);
}

/**
 * Apply the retailer's branding to the document:
 *   • set CSS variables `--brand-primary` / `--brand-secondary` on `:root`
 *   • derive source-of-truth tokens (`--brand`, `--brand-2`, `--brand-soft`,
 *     `--brand-strong`) from the retailer's primary colour so the dashboard
 *     gradient + tinted backgrounds restyle automatically per-retailer.
 *   • set the page title to "<RetailerName> — <FirstName>'s Project"
 *   • inject (or replace) a favicon link pointing at the retailer logo
 */
export function applyBranding(project: CustomerProject): void {
  const { retailer, customer, projectName } = project;

  const root = document.documentElement;
  // Always apply the full --brand* set so the theme is coherent on every load.
  // Use the retailer's PG primary colour; if they haven't set one, fall back to
  // the shared platform colour (#E8760A) rather than leaving a stale default.
  // 2026-06-10: this is the one colour that drives the whole customer portal.
  const primary = retailer.primaryColour || "#E8760A";
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand", primary);
  const rgb = parseHexToRgb(primary);
  if (rgb) {
    root.style.setProperty(
      "--brand-soft",
      `rgba(${rgb.r},${rgb.g},${rgb.b},0.10)`,
    );
    // Lighter sister for the hero gradient (right-hand stop).
    root.style.setProperty("--brand-2", lighten(rgb, 0.12));
    // Darker sister for hover states.
    root.style.setProperty("--brand-strong", darken(rgb, 0.18));
    // Readable text colour for content sitting ON the brand colour (e.g. the
    // customer's own message bubbles). Computed from the WCAG relative
    // luminance of the brand: dark brands (L < 0.5) get white text, light
    // brands get dark text. This guarantees legibility whatever colour the
    // retailer picks, instead of relying on a fixed --brand-secondary that
    // could end up dark-on-dark.
    root.style.setProperty(
      "--brand-on-primary",
      relativeLuminance(rgb) < 0.5 ? "#ffffff" : "#1F2937",
    );
  }
  if (retailer.secondaryColour) {
    root.style.setProperty("--brand-secondary", retailer.secondaryColour);
  }

  const first = (customer.firstName || "").trim();
  const projectLabel = projectName?.trim() || `${first ? `${first}'s` : "Your"} Project`;
  document.title = `${retailer.businessName} — ${projectLabel}`;

  if (retailer.logoUrl) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = retailer.logoUrl;
  }
}

// ── Normalisation ────────────────────────────────────────────────────────────
//
// The legacy server returns a slightly different shape than the strict
// `CustomerProject` we want internally. We do a permissive coercion here so
// sections can rely on the typed contract.

// The backend sends `project.quotes[]` (plural) where each item uses `ref`,
// `total`, `vat`, numeric `unit` prices, and an object status `{cls,label}`.
// The customer portal renders a single `project.quote` typed as CustomerQuote
// with a string status enum. Normalise the most-recent quote into that shape
// so it actually renders (Bug 3). Display-only — no pricing/commission logic.
function normaliseQuote(raw: Record<string, unknown>): CustomerQuote | null {
  const list = Array.isArray(raw.quotes) ? (raw.quotes as Record<string, unknown>[]) : [];
  const q = (list[0] ?? (raw.quote as Record<string, unknown> | null)) as Record<string, unknown> | null;
  return normaliseOneQuote(q);
}

/** Normalise EVERY quote the backend sent (Item 8 — customers must see all
 *  quotes, not just the most recent). Newest-first, matching the backend's
 *  `unshift` ordering. Singular consumers keep using `normaliseQuote` (latest).*/
function normaliseQuotes(raw: Record<string, unknown>): CustomerQuote[] {
  const list = Array.isArray(raw.quotes) ? (raw.quotes as Record<string, unknown>[]) : [];
  const fallback = (raw.quote as Record<string, unknown> | null) ?? null;
  const source = list.length ? list : (fallback ? [fallback] : []);
  return source
    .map((q) => normaliseOneQuote(q))
    .filter((q): q is CustomerQuote => q !== null);
}

function normaliseOneQuote(q: Record<string, unknown> | null): CustomerQuote | null {
  if (!q || typeof q !== "object") return null;

  const VALID = ["draft", "sent", "approved", "rejected", "expired"] as const;
  const deriveStatus = (s: unknown, accepted: unknown): CustomerQuote["status"] => {
    if (typeof s === "string" && (VALID as readonly string[]).includes(s)) {
      return s as CustomerQuote["status"];
    }
    if (accepted) return "approved";
    const blob =
      s && typeof s === "object"
        ? `${(s as { cls?: string }).cls ?? ""} ${(s as { label?: string }).label ?? ""}`.toLowerCase()
        : String(s ?? "").toLowerCase();
    if (/approv|accept|won|green/.test(blob)) return "approved";
    if (/reject|declin|lost/.test(blob)) return "rejected";
    if (/expir/.test(blob)) return "expired";
    if (/draft/.test(blob)) return "draft";
    return "sent"; // amber / "awaiting your review" / default
  };

  const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const itemsRaw = Array.isArray(q.items) ? (q.items as Record<string, unknown>[]) : [];
  const items: CustomerQuoteItem[] = itemsRaw.map((it, i) => ({
    id: str(it.id) || String(i),
    description: str(it.description ?? it.desc ?? it.code) || "Item",
    // Backend `unit` is a numeric unit-price, NOT a unit-of-measure label —
    // never surface it as the string `unit`. Use an explicit label if present.
    unit: nullableStr(it.unitLabel ?? it.uom),
    quantity: num(it.quantity ?? it.qty) || 1,
    lineTotal: num(it.lineTotal ?? it.total) || num(it.qty ?? it.quantity) * num(it.unit ?? it.price),
    roomName: nullableStr(it.roomName ?? it.room ?? it.zone),
  }));

  return {
    id: str(q.id) || str(q.ref),
    reference: str(q.reference ?? q.ref),
    status: deriveStatus(q.status, q.acceptedAt ?? q.approvedAt),
    projectName: nullableStr(q.projectName ?? q.title ?? q.project),
    rooms: [],
    items,
    subtotal: num(q.subtotal ?? q.sub),
    vatAmount: num(q.vatAmount ?? q.vat),
    totalIncVat: num(q.totalIncVat ?? q.total),
    deliveryCharge: num(q.deliveryCharge),
    validDays: num(q.validDays) || 30,
    sentAt: nullableStr(q.sentAt ?? q.date),
    approvedAt: nullableStr(q.acceptedAt ?? q.approvedAt),
    expiresAt: nullableStr(q.expiresAt),
    termsConditions: nullableStr(q.termsConditions ?? q.terms),
    retailerVatNumber: nullableStr(q.retailerVatNumber ?? q.vatNumber),
    introNote: nullableStr(q.introNote),
    closingNote: nullableStr(q.closingNote),
    header: normaliseQuoteHeader(q.header),
    bankDetails: normaliseBankDetails(q.bankDetails),
  };
}

/** Normalise the retailer's BACS block.
 *
 *  Returns null unless an account name AND number are both present — the
 *  server applies the same rule, and mirroring it here means a malformed or
 *  partially-populated payload can never render half a payment instruction.
 *  The acceptance gate lives on the server: this field is simply absent on a
 *  quote the customer has not accepted. */
function normaliseBankDetails(raw: unknown): CustomerQuote["bankDetails"] {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const accountName = str(o.accountName).trim();
  const accountNumber = str(o.accountNumber).trim();
  if (!accountName || !accountNumber) return null;
  return {
    accountName,
    accountNumber,
    sortCode: str(o.sortCode).trim(),
    referenceNote: str(o.referenceNote).trim(),
  };
}

/** Normalise the captured two-party header (null on pre-feature quotes). */
function normaliseQuoteHeader(raw: unknown): CustomerQuote["header"] {
  if (!raw || typeof raw !== "object") return null;
  const h = raw as Record<string, unknown>;
  const block = (b: unknown): { name: string; addressLines: string[] } & Record<string, unknown> => {
    const o = (b && typeof b === "object" ? b : {}) as Record<string, unknown>;
    const lines = Array.isArray(o.addressLines)
      ? (o.addressLines as unknown[]).map((l) => str(l)).filter((l) => l.trim() !== "")
      : [];
    return { ...o, name: str(o.name), addressLines: lines };
  };
  const s = block(h.seller);
  const b = block(h.buyer);
  return {
    seller: {
      name: s.name,
      addressLines: s.addressLines,
      phone: nullableStr(s.phone),
      email: nullableStr(s.email),
      vatNumber: nullableStr(s.vatNumber),
    },
    buyer: { name: b.name, addressLines: b.addressLines },
  };
}

/** Coerce the backend `journey` payload (Bug 4) into a typed ProjectJourney,
 *  or null when absent/malformed (the Home bar then doesn't render). */
function normaliseJourney(raw: unknown): ProjectJourney | null {
  if (!raw || typeof raw !== "object") return null;
  const j = raw as Record<string, unknown>;
  const rawStages = Array.isArray(j.stages) ? (j.stages as Record<string, unknown>[]) : [];
  const stages: JourneyStage[] = rawStages
    .map((s) => {
      const state = String(s.state ?? "");
      return {
        key: str(s.key),
        label: str(s.label),
        state: (state === "done" || state === "current" ? state : "upcoming") as JourneyStage["state"],
      };
    })
    .filter((s) => s.key && s.label);
  if (!stages.length) return null;
  return {
    supplyType: j.supplyType === "supply_only" ? "supply_only" : "supply_install",
    currentKey: str(j.currentKey),
    stages,
  };
}

function normaliseProject(env: ProjectEnvelope): CustomerProject {
  // The legacy `/api/customer/project` puts customer at the top level and
  // wraps everything else under `project`. We don't have a reliable shape
  // for the legacy `project` field yet, so we treat it as a partial and
  // fill missing pieces with safe defaults. Real strict typing arrives
  // when the API is refactored to return `CustomerProject` natively.
  const raw = (env.project ?? {}) as Partial<CustomerProject> & Record<string, unknown>;
  const customerRaw = (env.customer ?? {}) as Record<string, unknown>;

  // First name for greetings — strip a leading honorific (Mr/Mrs/Dr/…) and
  // take the first real name token, so "Mr ZZZZ"/"Mr John Smith" greet as
  // "ZZZZ"/"John", never "Welcome Mr". (Bug 7)
  const fullNameForFirst =
    str(customerRaw.displayName) ||
    [
      str(customerRaw.firstNames ?? customerRaw.firstName ?? customerRaw.first_name),
      str(customerRaw.surname ?? customerRaw.lastName ?? customerRaw.last_name),
    ].filter(Boolean).join(" ");

  const customer = {
    id: str(customerRaw.id),
    // The legacy `/api/customer/project` (publicCustomer) exposes the name as
    // `firstNames` (plural) + `surname`, not firstName/lastName — fall back to
    // those (and finally `displayName`) so the welcome hero + header greeting
    // aren't left blank for real customers.
    firstName: firstNameFrom(fullNameForFirst),
    lastName: str(customerRaw.lastName ?? customerRaw.last_name ?? customerRaw.surname),
    email: nullableStr(customerRaw.email),
    phone: nullableStr(customerRaw.phone),
    address: customerRaw.address ?? null,
    // postcode is stored separately from address (Issue 7) — carry it through
    // so an edited postcode persists on reload (was dropped here, blanking the
    // Account page postcode even after a successful save).
    postcode: str(customerRaw.postcode),
    // First-open T&C gate flag — null until the customer accepts.
    termsAcceptedAt: nullableStr(customerRaw.termsAcceptedAt),
  };

  const retailer = (raw.retailer ?? buildFallbackBranding(raw)) as RetailerBranding;

  const project: CustomerProject = {
    customer,
    retailer,
    status: (raw.status as CustomerProject["status"]) ?? "lead",
    journey: normaliseJourney(raw.journey),
    projectName: nullableStr(raw.projectName),
    installDate: nullableStr(raw.installDate),
    quote: normaliseQuote(raw),
    quotes: normaliseQuotes(raw),
    paymentSchedule: asArray<CustomerProject["paymentSchedule"][number]>(raw.paymentSchedule),
    designs: asArray<CustomerProject["designs"][number]>(raw.designs),
    deliveries: asArray<CustomerProject["deliveries"][number]>(raw.deliveries),
    messages: asArray<CustomerProject["messages"][number]>(raw.messages),
    documents: asArray<CustomerProject["documents"][number]>(raw.documents),
    bookings: asArray<CustomerProject["bookings"][number]>(raw.bookings),
    warrantyItems: asArray<CustomerProject["warrantyItems"][number]>(raw.warrantyItems),
  };

  // The server computes the unread-inbound count (messages live in unified
  // messaging, not in project.messages — that array is always empty — so the
  // home "New messages" card has no other source). Without carrying this field
  // through, the card falls back to counting the empty messages[] and is stuck
  // at 0. With it mapped, the shell's change-detected 5s poll also picks up a
  // new message and updates the card automatically.
  if (typeof raw.unreadMessages === "number") project.unreadMessages = raw.unreadMessages;
  // Global customer-render feature flag (top-level on the envelope, not under
  // `project`) — drives whether the designs section shows render content.
  project.customerRenderEnabled = env.customerRenderEnabled === true;
  return project;
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function buildFallbackBranding(raw: Record<string, unknown>): RetailerBranding {
  const brand = (raw.brand ?? {}) as Record<string, unknown>;
  return {
    id: str(brand.id ?? raw.retailerId),
    businessName: str(brand.businessName ?? brand.name ?? raw.retailerName) || "Your Retailer",
    logoUrl: nullableStr(brand.logoUrl ?? brand.logo),
    primaryColour: str(brand.primaryColour ?? brand.primary) || "#E8760A",
    secondaryColour: str(brand.secondaryColour ?? brand.secondary) || "#1F2937",
    phone: nullableStr(brand.phone ?? raw.retailerPhone),
    email: nullableStr(brand.email ?? raw.retailerEmail),
    twilioNumber: nullableStr(brand.twilioNumber),
    googleReviewUrl: nullableStr(brand.googleReviewUrl ?? raw.googleReviewUrl),
  };
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

// Honorifics we drop from the greeting so customers aren't greeted by title.
const NAME_TITLES = new Set([
  "mr", "mrs", "ms", "miss", "mx", "dr", "prof", "sir", "madam", "master", "mstr", "rev",
]);
/** First real name token from a full name, dropping a leading honorific. */
function firstNameFrom(full: string): string {
  const tokens = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length > 1 && NAME_TITLES.has(tokens[0].replace(/\.$/, "").toLowerCase())) {
    tokens.shift();
  }
  return tokens[0] || "";
}

function nullableStr(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t === "" ? null : t;
}

// ── Colour helpers (branding) ────────────────────────────────────────────────

interface Rgb { r: number; g: number; b: number; }

function parseHexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1]!;
  if (h.length === 3) {
    return {
      r: parseInt(h[0]! + h[0]!, 16),
      g: parseInt(h[1]! + h[1]!, 16),
      b: parseInt(h[2]! + h[2]!, 16),
    };
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

/** WCAG 2.x relative luminance (0 = black, 1 = white) for an sRGB colour.
 *  Used to decide whether text on a brand-coloured surface should be light
 *  or dark. */
function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number): number => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function lighten(rgb: Rgb, amount: number): string {
  const r = clamp255(rgb.r + (255 - rgb.r) * amount);
  const g = clamp255(rgb.g + (255 - rgb.g) * amount);
  const b = clamp255(rgb.b + (255 - rgb.b) * amount);
  return `rgb(${r},${g},${b})`;
}

function darken(rgb: Rgb, amount: number): string {
  return `rgb(${clamp255(rgb.r * (1 - amount))},${clamp255(rgb.g * (1 - amount))},${clamp255(rgb.b * (1 - amount))})`;
}

// ── Demo fallback ────────────────────────────────────────────────────────────
//
// Used when the backend is unreachable or returns 401/404 for the supplied
// token. Numbers and shapes mirror what the legacy demo seed produces so
// sibling section modules can be exercised end-to-end without a server.

/** Build a fully-populated `CustomerProject` for local demo / dev. */
export function buildDemoProject(): CustomerProject {
  // jsonb-shaped address — exercises every branch of `formatAddress`.
  const demoAddress: Record<string, string> = {
    line1: "14 Cold Bath Road",
    line2: "Harlow Hill",
    city: "Harrogate",
    county: "North Yorkshire",
    postcode: "HG2 0NA",
  };

  // Smoke-check: assert formatAddress returns a non-placeholder string for
  // the demo address. If a future change to formatAddress regresses the
  // jsonb path, dev console will warn loudly.
  const formatted = formatAddress(demoAddress);
  if (formatted === "—") {
    console.warn("[customer/demo] formatAddress did not render the demo address");
  }

  const retailer: RetailerBranding = {
    id: "ret_demo_turner",
    businessName: "Turner Kitchens",
    logoUrl: null,
    primaryColour: "#0d8a72",
    secondaryColour: "#1F2937",
    // `phone` here is the retailer's personal line — kept on the type so
    // the schema round-trips, but the customer portal MUST NOT render it.
    // Only `twilioNumber` (the platform-allocated number that routes
    // through TKOS) is shown to customers ([[feedback-contact-masking]]).
    phone: "01423 555 0188",
    email: "hello@turner-kitchens.co.uk",
    twilioNumber: "020 3893 1188",
    googleReviewUrl:
      "https://search.google.com/local/writereview?placeid=ChIJN1t_tDeuEmsRUsoyG83frY4",
  };

  const project: CustomerProject = {
    customer: {
      id: "cust_demo_sarah",
      firstName: "Sarah",
      lastName: "Mitchell",
      email: "sarah.mitchell@example.co.uk",
      phone: "07700 900123",
      address: demoAddress,
    },
    retailer,
    status: "approved",
    journey: {
      supplyType: "supply_install",
      currentKey: "order_confirmed",
      stages: [
        { key: "enquiry", label: "Enquiry", state: "done" },
        { key: "design", label: "Design", state: "done" },
        { key: "quote_sent", label: "Quote Sent", state: "done" },
        { key: "order_confirmed", label: "Order Confirmed", state: "current" },
        { key: "delivery", label: "Delivery", state: "upcoming" },
        { key: "installation", label: "Installation", state: "upcoming" },
        { key: "complete", label: "Complete", state: "upcoming" },
      ],
    },
    projectName: "Mitchell Kitchen — Harrogate",
    installDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
    quote: {
      id: "quote_demo_mitchell",
      reference: "KOS-Q-2026-0184",
      status: "approved",
      projectName: "Mitchell Kitchen — Harrogate",
      rooms: [
        { id: "rm_kitchen", name: "Kitchen", subtotal: 19000 },
        { id: "rm_utility", name: "Utility", subtotal: 5500 },
      ],
      items: [
        {
          id: "li_1",
          description: "Shaker base units (oak)",
          unit: "lin/m",
          quantity: 6,
          lineTotal: 7800,
          roomName: "Kitchen",
        },
        {
          id: "li_2",
          description: "Quartz worktop install",
          unit: "lin/m",
          quantity: 5.4,
          lineTotal: 4200,
          roomName: "Kitchen",
        },
        {
          id: "li_3",
          description: "Bosch induction hob & extractor",
          unit: null,
          quantity: 1,
          lineTotal: 1900,
          roomName: "Kitchen",
        },
        {
          id: "li_4",
          description: "Tall larder cabinet",
          unit: null,
          quantity: 1,
          lineTotal: 5100,
          roomName: "Kitchen",
        },
        {
          id: "li_5",
          description: "Utility cabinetry & sink",
          unit: null,
          quantity: 1,
          lineTotal: 5500,
          roomName: "Utility",
        },
      ],
      subtotal: 20416.67,
      vatAmount: 4083.33,
      totalIncVat: 24500,
      deliveryCharge: 0,
      validDays: 30,
      sentAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      approvedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
      // This demo quote is `status: "approved"`, which is exactly the state
      // that earns the bank block — so the dev-only fallback exercises it.
      // Fictional details; never a real account.
      bankDetails: {
        accountName: "Demo Kitchens Ltd",
        sortCode: "00-00-00",
        accountNumber: "00000000",
        referenceNote: "Please quote KOS-Q-2026-0184 as your payment reference.",
      },
      expiresAt: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString(),
      termsConditions: null,
      retailerVatNumber: null,
      introNote: null,
      closingNote: null,
      header: null,
    },
    // Demo fixture: the single demo quote is surfaced via the `quote` fallback
    // in the Quote section's mount(), so an empty list here is sufficient.
    quotes: [],
    paymentSchedule: [
      {
        id: "pay_1",
        stageName: "Deposit",
        percentage: 10,
        amount: 2450,
        triggerEvent: "on_order",
        dueDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        status: "paid",
        stripePaymentIntentId: "pi_demo_deposit",
      },
      {
        id: "pay_2",
        stageName: "Pre-delivery",
        percentage: 40,
        amount: 9800,
        triggerEvent: "before_delivery",
        dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        status: "paid",
        stripePaymentIntentId: "pi_demo_predelivery",
      },
      {
        id: "pay_3",
        stageName: "Final payment",
        percentage: 50,
        amount: 12250,
        triggerEvent: "on_completion",
        dueDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: null,
        status: "pending",
        stripePaymentIntentId: null,
      },
    ],
    designs: [
      {
        id: "design_1",
        filename: "mitchell-kitchen-3d-render-v3.pdf",
        fileType: "application/pdf",
        fileUrl: "/demo/designs/mitchell-3d-v3.pdf",
        fileSize: 3_400_000,
        notes: "Final 3D render after island worktop overhang revision.",
        uploadedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: "design_2",
        filename: "mitchell-floorplan.pdf",
        fileType: "application/pdf",
        fileUrl: "/demo/designs/mitchell-floorplan.pdf",
        fileSize: 980_000,
        notes: "Approved floor plan with utility door swing change.",
        uploadedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    deliveries: [
      {
        id: "del_1",
        kosReference: "KOS-D-2026-0091",
        description: "Cabinetry — main kitchen run",
        carrier: "TKOS Driver",
        trackingNumber: "KOS15004477719",
        eta: new Date(Date.now() + 90 * 60 * 1000).toISOString(),
        status: "out_for_delivery",
        events: [
          {
            status: "scheduled",
            description: "Pickup booked from Turner Kitchens warehouse",
            at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          },
          {
            status: "in_transit",
            description: "Collected — en route to Harrogate",
            at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          },
          {
            status: "out_for_delivery",
            description: "On the van for delivery today",
            at: new Date(Date.now() - 35 * 60 * 1000).toISOString(),
          },
        ],
        // Driver currently in Leeds heading north to Harrogate.
        driverLocation: {
          lat: 53.8489,
          lng: -1.5388,
          driverName: "Mark",
          updatedAt: new Date(Date.now() - 90 * 1000).toISOString(),
          heading: 15,
        },
        // Harrogate destination (Cold Bath Road).
        destination: { lat: 53.9921, lng: -1.5443 },
      },
    ],
    messages: [
      {
        id: "msg_1",
        direction: "inbound",
        subject: "Final payment reminder",
        body: "Hi Sarah — install is on for the 7th. We'll send the final invoice once cabinetry is on-site.",
        channel: "in_app",
        sentAt: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
        read: true,
      },
      {
        id: "msg_2",
        direction: "outbound",
        subject: "Re: Final payment reminder",
        body: "Brilliant, thank you! I'll be home from 8am on the 7th.",
        channel: "in_app",
        sentAt: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
        read: true,
      },
    ],
    documents: [
      {
        id: "doc_1",
        filename: "turner-kitchens-terms-2026.pdf",
        fileType: "application/pdf",
        fileUrl: "/demo/docs/turner-terms-2026.pdf",
        fileSize: 145_000,
        notes: "Standard terms & conditions for your order.",
        uploadedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    bookings: [
      {
        id: "bk_demo_1",
        visitType: "site_survey",
        preferredDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10),
        preferredTime: "10:30",
        notes: "Front door access — please use side gate.",
        status: "confirmed",
        requestedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        confirmedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000 + 10.5 * 60 * 60 * 1000)
          .toISOString(),
      },
    ],
    warrantyItems: [],
  };

  return project;
}
